import { addDays, startOfDay } from "date-fns";
import { Box, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import SectionCollapsible from "../../../../common/SectionCollapsible";
import {
  isUnitOfSaleOutcomeFilledOut,
  isUnitOfSaleSelected,
  outcomesTouched,
} from "../../../utils";
import type { AddSolutionFormValues } from "../../../add/form/types";
import type { EditSolutionFormValues } from "../../../edit/form/types";
import AddAnother from "../add-another/AddAnother";
import Outcome from "./Outcome";
import { OutcomeType } from "../../types";

function OutcomesContainer({ open }: { open: boolean }) {
  const context = useFormikContext<
    AddSolutionFormValues | EditSolutionFormValues
  >();
  const outcomes = context.values.outcomes;
  const hasLegacyEpochSavedOutcome = outcomes.some(
    (outcome) =>
      outcome.origin === "saved" && outcome["outcome-start-date"] === 0,
  );

  return (
    <Box data-testid="add-outcomes-container">
      <SectionCollapsible open={open} label="Outcomes">
        {hasLegacyEpochSavedOutcome && (
          <Box sx={{ padding: "0 3 2" }}>
            <Typography variant="subtitle2" align="left">
              Legacy start dates
            </Typography>
            <Typography variant="body2" align="left" sx={{ mb: 2 }}>
              Some existing outcomes had no recorded start date, so they show as
              01/01/1970 until a real start date is entered.
            </Typography>
          </Box>
        )}
        <AddAnother<OutcomeType>
          name="outcomes"
          addLabel="Add another"
          initialCounter={1}
          getCounter={(outcome) => outcome.outcomeCounter}
          createValue={(outcomeCounter, currentOutcomes) => {
            const latestOutcome = [...currentOutcomes].sort(
              (firstOutcome, secondOutcome) =>
                firstOutcome.outcomeCounter - secondOutcome.outcomeCounter,
            )[currentOutcomes.length - 1];
            const newOutcomeStartDate = latestOutcome?.["outcome-end-date"]
              ? startOfDay(
                  addDays(new Date(latestOutcome["outcome-end-date"]), 1),
                ).getTime()
              : null;

            return {
              unit: "",
              value: "",
              "unit-of-sale": false,
              "outcome-start-date": newOutcomeStartDate,
              "outcome-end-date": null,
              outcomeCounter,
              origin: "draft" as const,
            };
          }}
          compareValues={(firstOutcome, secondOutcome) =>
            firstOutcome.outcomeCounter - secondOutcome.outcomeCounter
          }
          renderItem={({ value, index, remove }) => (
            <Box sx={{ padding: "0 3" }}>
              <Outcome value={value} index={index} remove={remove} />
            </Box>
          )}
          beforeAddContent={
            <>
              {outcomesTouched(context.values) &&
                !isUnitOfSaleSelected(context.values) && (
                  <Typography color="error" variant="subtitle2" align="left">
                    The unit of sale must be selected.
                  </Typography>
                )}
              {outcomesTouched(context.values) &&
                isUnitOfSaleSelected(context.values) &&
                !isUnitOfSaleOutcomeFilledOut(context.values) && (
                  <Typography color="error" variant="subtitle2" align="left">
                    The Outcome tagged as unit of sale must have a unit and a
                    value selected.
                  </Typography>
                )}
            </>
          }
        />
      </SectionCollapsible>
    </Box>
  );
}

export default OutcomesContainer;
